import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { SERVICES, IMAGES } from "@/data/content";

const ServiceItem = ({ service, index, open, onToggle }) => (
  <div className="border-b border-ink/15" data-testid={`service-item-${index + 1}`}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`service-toggle-${index + 1}`}
      className="w-full flex items-baseline gap-6 md:gap-10 py-8 md:py-10 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-burgundy"
    >
      <span className="font-serif text-sm md:text-base text-burgundy/60 shrink-0 w-8">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span
        className={`flex-1 font-serif text-2xl md:text-4xl tracking-tight leading-snug transition-colors duration-300 ${
          open ? "text-burgundy" : "text-ink group-hover:text-burgundy"
        }`}
      >
        {service.title}
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="shrink-0 self-center"
      >
        <Plus className="h-6 w-6 text-ink/50" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-10 md:pb-14 pl-14 md:pl-[4.5rem] pr-2 max-w-4xl">
            <p className="text-base leading-relaxed text-ink/70">{service.intro}</p>
            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">
              Services may include
            </p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {service.services.map((s) => (
                <li
                  key={s}
                  className="text-xs md:text-sm text-ink/70 border border-ink/15 bg-white px-3 py-1.5"
                >
                  {s}
                </li>
              ))}
            </ul>
            {service.note && (
              <p className="mt-8 text-sm leading-relaxed text-ink/60 border-l-2 border-gold pl-5 italic">
                {service.note}
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const WhatWeDo = () => {
  const [open, setOpen] = useState(0);

  return (
    <>
      <SEO
        title="What We Do | Strategic Communications, Public Affairs & Engagement — Mosaicon Strategies"
        description="Strategic counsel and execution across communications, media relations, public affairs, government relations, stakeholder engagement, multicultural outreach, crisis communications and events in the Greater Toronto Area."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 md:pt-28 md:pb-20" data-testid="whatwedo-hero">
        <Overline>What We Do</Overline>
        <h1 className="mt-6 font-serif text-4xl md:text-6xl tracking-tighter text-ink leading-[1.08] max-w-4xl" data-testid="whatwedo-heading">
          <MaskedLines
            delay={0.15}
            lines={["Strategy is only valuable when", <>it moves people to <em className="text-burgundy">action</em>.</>]}
          />
        </h1>
        <Fade delay={0.3} className="mt-8 max-w-2xl space-y-5 text-base md:text-lg leading-relaxed text-ink/70" data-testid="whatwedo-intro">
          <p>
            Every organization communicates. The challenge is ensuring that communications advance the right
            objective, reach the right people and strengthen rather than complicate relationships.
          </p>
          <p>
            Mosaicon provides strategic counsel and execution across communications, public affairs, stakeholder
            engagement and reputation.
          </p>
        </Fade>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32" data-testid="services-list">
        <Fade>
          <div className="border-t border-ink/15">
            {SERVICES.map((service, i) => (
              <ServiceItem
                key={service.title}
                service={service}
                index={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </div>
        </Fade>
      </section>

      <section className="relative overflow-hidden bg-teal text-cream" data-testid="whatwedo-cta">
        <img
          src={IMAGES.mosaicTexture}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
          loading="lazy"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl">
            Not sure which discipline your challenge belongs to?
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/75">
            Most mandates cross boundaries. Tell us what you are trying to accomplish and we will help you see
            how the pieces connect.
          </p>
          <Link
            to="/contact"
            data-testid="whatwedo-contact-btn"
            className="group mt-10 inline-flex items-center gap-3 bg-cream text-teal text-xs uppercase tracking-[0.22em] font-bold px-10 py-5 transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            Start a Conversation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default WhatWeDo;
